import { QueryClient, hashKey, type QueryKey } from '@tanstack/react-query';

import { deriveActingContext, type OperatorSessionSnapshot } from './acting/context';
import { useFirmActStore } from './firm-act-store';
import { useImpersonationStore } from './impersonation-store';

/**
 * The identity whose data is currently cached.
 *
 * Every one of the ~312 query keys in this app is written workspace-blind:
 * `['dashboard']`, `['trusts', 'list']`, `['inbox']`. Nothing in the key says
 * WHOSE dashboard. So when a login changed workspace, the cache happily served
 * the previous firm's book under the new firm's screen — a cross-tenant read,
 * in a product where that is the one failure it cannot have.
 *
 * The 2026-08-09 fix cleared the cache on switch. That works only while every
 * future switch path remembers to call it, which is the same "rule written down
 * rather than mechanised" shape that keeps costing this codebase. The fix after
 * it folded a principal into the cache key hash, so two workspaces cannot
 * address the same cache entry no matter what the call site wrote.
 *
 * WHAT WAS STILL WRONG, AND WHAT ARC-01 T6 CHANGES.
 *
 * That principal was built from the SUPABASE SESSION ALONE (`userId:tenantId`,
 * written by `lib/auth-context.tsx` on auth events). Act-as and impersonation
 * never touch the Supabase session. So the operator, client A and client B all
 * addressed the SAME cache entries, and the product carried two hand-written
 * mitigations for what is a global fact:
 *
 *   1. `routes/admin/client-book-screen.tsx` had a private
 *      `resetClientScopedCache()` with a KEEP_ROOTS allowlist, called on the
 *      three act-as entry points in that one file.
 *   2. `components/firm-act-bar.tsx` called a blanket `qc.clear()` on exit.
 *
 * And IMPERSONATION HAD NEITHER. A super_admin who read `/superadmin/tenants/$id`
 * and then impersonated a user landed on `/app/dashboard` with the previous
 * tenant's queries already warm at a 30s staleTime. That is a live cross-tenant
 * read in a POPIA-regulated product, and no amount of per-screen patching finds
 * the next entry point before a user does.
 *
 * The principal is now `deriveActingContext(...).principalKey` (see
 * `lib/acting/context.ts`), which changes on every posture or subject change. It
 * is recomputed from THREE inputs:
 *
 *   1. the Supabase session, pushed in by `setQueryPrincipal` on auth events;
 *   2. the act-as store, read through a SUBSCRIPTION;
 *   3. the impersonation store, likewise.
 *
 * The subscription is the point. Posture is written from eight lines across
 * three files and cleared from five more (`lib/act-as-context.ts`,
 * `lib/sign-out.ts`, `routes/admin/_layout.tsx`, `routes/superadmin/_layout.tsx`,
 * `lib/onboarding-reset.ts`, the renewal hook, and both context bars). Asking
 * each of them to also re-point the cache is the same forgettable rule in new
 * clothes, so none of them is asked: writing the store IS re-pointing the cache,
 * and an entry point that has not been written yet is covered on the day it is.
 *
 * RE-KEYING, NOT EVICTING, AND THE DIFFERENCE MATTERS.
 *
 * Changing the principal makes the previous principal's entries UNREACHABLE. It
 * does not delete them. `qc.clear()` threw away the operator's own still-valid
 * console data along with the client's; re-keying leaves that data where it is
 * and instantly re-addressable the moment the operator is themselves again, so
 * closing an inline edit drawer no longer cold-starts the whole Client Book.
 * Abandoned entries have no observers and TanStack garbage-collects them on the
 * ordinary `gcTime`, so nothing accumulates.
 *
 * Sign-out is the one place that still EVICTS on purpose (`lib/sign-out.ts`
 * calls `queryClient.clear()`), because there the point is to leave nothing in
 * memory on a shared machine, not merely to make it unaddressable.
 *
 * KNOWN COST, STATED RATHER THAN HIDDEN. The principal is global, but the
 * AUDIENCE of an individual call is not: roughly 30 calls pass
 * `useOperatorSession: true` and travel on the operator's own session even while
 * a client is being administered. Those land under the SUBJECT's principal all
 * the same, so the Client Book's own queries go cold when the inline edit drawer
 * mints an act-as token without navigating, and refetch. They are correct, they
 * are the operator's own data, and no other principal can read them; the cost is
 * one round trip of loading state on that screen. The real remedy is ARC-01 T7
 * (`audience: 'operator' | 'subject'` on ApiRequestOptions), which lets an
 * operator-audience call keep the operator's principal. Doing it here instead
 * would mean a second KEEP_ROOTS allowlist inside the hash function, where
 * getting an entry wrong is a leak rather than a flash.
 *
 * Deliberately NOT a React state value — `queryKeyHashFn` is called
 * synchronously deep inside the query cache and cannot read hooks.
 */
let activePrincipal = 'anonymous';

/**
 * The operator third of the principal: the last Supabase session the auth layer
 * reported. The other two are read live off the stores.
 *
 * `displayName` and `rawRole` are placeholders because `principalKey` uses
 * neither (it is built from the Supabase user id plus the subject). Inventing a
 * name or a role here would put a second source of truth for both into the cache
 * layer, which is the bug class ARC-01 exists to close.
 */
let operatorSnapshot: OperatorSessionSnapshot | null = null;

/**
 * ⚠ DERIVED LAZILY, AND THAT IS NOT AN OPTIMISATION. IT IS THE IMPORT CYCLE.
 *
 * `lib/acting/context.ts` imports `use-session` → `auth-context` →
 * `onboarding-reset` → back to this module, and it does so BEFORE it imports
 * `authz/route-policy`. So while that chain is unwinding, `deriveActingContext`
 * is callable (a hoisted function declaration) but `normalizeRole` inside it is
 * still in its temporal dead zone. An eager derivation at module scope here
 * threw `Cannot access '__vite_ssr_import_5__' before initialization` and took 11
 * suites down with it.
 *
 * Deriving on READ instead of on WRITE moves the first call to after every
 * module in the cycle has finished, which is any time a query key is hashed. The
 * stores are still the trigger: a write only marks the principal stale.
 */
let principalStale = true;

/** Cheap enough to run on every store write: it sets one boolean. */
function markQueryPrincipalStale(): void {
  principalStale = true;
}

function currentPrincipal(): string {
  if (principalStale) {
    activePrincipal = deriveActingContext({
      operator: operatorSnapshot,
      firmAct: useFirmActStore.getState().active,
      impersonation: useImpersonationStore.getState().active,
    }).principalKey;
    principalStale = false;
  }
  return activePrincipal;
}

/**
 * Set by the auth layer whenever the SESSION changes. Idempotent.
 *
 * This no longer decides the principal on its own: it updates the operator third
 * of it and re-derives. Signing back in as the same user in the same workspace
 * therefore lands on the same principal string and REUSES that operator's cache
 * rather than cold-starting every screen.
 */
export function setQueryPrincipal(input: { userId?: string | null; tenantId?: string | null }): void {
  operatorSnapshot = input.userId
    ? {
        supabaseUserId: input.userId,
        displayName: null,
        rawRole: '',
        tenantId: input.tenantId ?? null,
      }
    : null;
  markQueryPrincipalStale();
}

/** Current principal — exported for tests and diagnostics. */
export function getQueryPrincipal(): string {
  return currentPrincipal();
}

/**
 * Hash a query key WITH the active principal.
 *
 * Note this changes cache identity only. `invalidateQueries({ queryKey })` and
 * friends still match on the key array itself, so existing invalidation code is
 * untouched — it simply also sweeps the other principal's now-unreachable
 * entries, which is what we want anyway.
 */
function principalScopedHash(queryKey: QueryKey): string {
  return hashKey([currentPrincipal(), ...queryKey]);
}

/**
 * Default QueryClient for the Elreign frontend.
 *
 * Defaults chosen for B2B trust-admin UX:
 * - 30s staleTime — fiduciary dashboards aren't real-time-streamed; aggressive
 *   refetch wastes RU/s on Cosmos and confuses operators with shifting numbers.
 * - retry: 4xx never retried (won't change); 5xx + network retry up to 2x.
 * - no auto-refetch on window focus (operator switches tabs constantly while
 *   reviewing — refocus-driven refetch would shift visible numbers mid-review).
 * - queryKeyHashFn scopes every cache entry to the active principal (above).
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryKeyHashFn: principalScopedHash,
      staleTime: 30_000,
      retry: (failureCount, error) => {
        const status = (error as { status?: number })?.status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});

// The mechanism, installed at module scope, so it is live for anything that can
// reach the cache: importing the cache IS importing its scoping. There is no
// `installCachePrincipal()` for a future entry point to forget to call, and no
// ordering requirement against React mount.
//
// Both stores are sessionStorage-backed and zustand rehydrates them
// synchronously at store creation, so the first derivation reads the value an
// in-tab reload restored rather than the empty initial state. `principalStale`
// starts true, so no seeding call is needed and none may be added: see the
// import-cycle note above.
useFirmActStore.subscribe(markQueryPrincipalStale);
useImpersonationStore.subscribe(markQueryPrincipalStale);
